import { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

// 사이드바 라벨 통일에 맞춰 권한 키를 갱신한 후의 호환 매핑.
// 신규 키 → 동등 권한을 부여하던 구 키 목록. 양쪽 중 하나라도 true 면 통과.
export const LEGACY_MENU_ALIASES_MAP = {
  '제품 견적서': ['견적서 목록'],
  '서비스견적서': ['견적서 목록'],
  '승인상태': ['승인 요청'],
  '제품 묶음': ['묶음 카탈로그'],
  '상품 관리': ['제품 관리'],
  '서비스 관리': ['서비스 패키지'],
  '직무별 가격 관리': ['직무·등급 관리'],
  '사용자관리': ['사용자 관리'],
};

// 액션 권한 재분류 (생성·수정 통합, 삭제 분리) — 신규 키 → 구 키 alias 매핑.
// 신규 키 중 하나라도 true 면 통과. 기존 그룹의 구 권한 데이터를 그대로 호환.
export const LEGACY_ACTION_ALIASES_MAP = {
  '제품 견적서 생성·수정':  ['견적서 작성', '견적서 발행', '제품 견적서 작성', '제품 견적서 발행'],
  '제품 견적서 삭제':        ['견적서 삭제', '제품 견적서 삭제'],
  '서비스견적서 생성·수정': ['견적서 작성', '견적서 발행', '서비스견적서 작성', '서비스견적서 발행'],
  '서비스견적서 삭제':       ['견적서 삭제', '서비스견적서 삭제'],
  '제품 묶음 생성·수정':     ['묶음 등록/편집'],
  '제품 묶음 삭제':          ['묶음 삭제'],
  '상품 생성·수정':          ['제품 등록/편집'],
  '상품 삭제':               ['상품·원가기준표 삭제'],
  '원가기준표 관리':         ['원가기준표 생성', '원가기준표 Active 전환'],
  '원가기준표 삭제':         ['상품·원가기준표 삭제'],
  '자재 생성·수정':          ['자재 등록/편집'],
  '서비스 생성·수정':        ['서비스 패키지 등록/편집'],
  '서비스 삭제':             ['서비스 패키지 삭제'],
  '직무·등급 생성·수정':    ['직무·등급 등록/편집'],
  '고객 생성·수정':          ['고객 등록/편집'],
  '조직 생성·수정':          ['조직 관리'],
  '조직 삭제':               ['조직 관리'],
  '사용자 생성·수정':        ['사용자 관리'],
  '사용자 삭제':             ['사용자 관리'],
  '시스템 설정 변경':        ['시스템 설정'],
};

export function PermissionProvider({ children }) {
  const { user } = useAuth();

  const menuPerms = user?.menu_permissions || {};
  const actionPerms = user?.action_permissions || {};

  // 양방향 alias 검사: 신규 키 → 구 키, 구 키 → 신규 키
  const canAccessMenu = (menuName) => {
    if (menuPerms.all) return true;
    if (menuPerms[menuName]) return true;
    // 신규 → 구
    const fwd = LEGACY_MENU_ALIASES_MAP[menuName] || [];
    if (fwd.some(k => !!menuPerms[k])) return true;
    // 구 → 신규
    for (const [newKey, oldKeys] of Object.entries(LEGACY_MENU_ALIASES_MAP)) {
      if (oldKeys.includes(menuName) && menuPerms[newKey]) return true;
    }
    return false;
  };

  // 양방향 alias 검사: 신규 키 호출 → 구 키도 검사 / 구 키 호출 → 신규 키도 검사
  const canPerformAction = (actionName) => {
    if (actionPerms.all) return true;
    if (actionPerms[actionName]) return true;
    // 신규 → 구
    const fwd = LEGACY_ACTION_ALIASES_MAP[actionName] || [];
    if (fwd.some(k => !!actionPerms[k])) return true;
    // 구 → 신규 (역방향: 어떤 신규 키의 alias 목록에 actionName 이 들어있는지)
    for (const [newKey, oldKeys] of Object.entries(LEGACY_ACTION_ALIASES_MAP)) {
      if (oldKeys.includes(actionName) && actionPerms[newKey]) return true;
    }
    return false;
  };

  const isAdmin = menuPerms.all === true;

  return (
    <PermissionContext.Provider value={{ canAccessMenu, canPerformAction, isAdmin }}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  return useContext(PermissionContext);
}
